/**
 * The modules a builder has been assigned, as the way into each one.
 *
 * A builder reached their work through the same programme-then-module pair of
 * selects everyone else used, which asked them to pick a programme they may
 * only own one module of.
 *
 * @module components/programmes/AssignedModuleList
 */

import React from "react";

import { moduleReviewTone } from "@/components/shared/status-tones";
import { TONE_BADGE, TONE_ICON } from "@/components/shared/tone-presentation";
import { Badge, Button, EmptyState, EntityCard, Icon, MutedText, Time } from "@/components/ui";
import type { AssignedModuleSummary } from "@/platform";

/** The status as a pill, so a screen never picks the colour on its own. */
function ReviewBadge({ status }: { status: string }): React.ReactElement {
  const tone = moduleReviewTone(status);
  return (
    <Badge tone={TONE_BADGE[tone]} size="sm">
      <Icon name={TONE_ICON[tone]} aria-hidden />
      {status}
    </Badge>
  );
}

export interface AssignedModuleListProps {
  modules: readonly AssignedModuleSummary[];
  onOpen: (programmeId: string, moduleId: string) => void;
}

/**
 * @example
 * ```tsx
 * <AssignedModuleList modules={platformModules} onOpen={openModule} />
 * ```
 */
export function AssignedModuleList({
  modules,
  onOpen,
}: AssignedModuleListProps): React.ReactElement {
  if (modules.length === 0) {
    return (
      <EmptyState
        icon="squares-four"
        title="No modules assigned yet"
        description="A Programme Lead assigns the modules you build. They appear here once they do."
        data-testid="assigned-modules-empty"
      />
    );
  }

  return (
    <>
      {/* Named and counted. Not `CollectionView`: that one needs `columns`
          and draws a table above `md`, and this list has no table form — so
          what it was missing is the list semantics, not the composition. */}
      <div role="status" className="mb-2">
        <MutedText size="caption">
          {modules.length} {modules.length === 1 ? "module" : "modules"} assigned to you
        </MutedText>
      </div>
      <div
        role="list"
        aria-label="Modules assigned to you"
        className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
        data-testid="assigned-modules"
      >
        {modules.map((module) => (
          <div role="listitem" key={module.moduleId} className="min-w-0">
            <EntityCard
              eyebrow={module.programmeTitle}
              // The title, not the card: `EntityCard`'s `onSelect` reports a
              // pressed state, which is for a list that selects. This one
              // navigates, so every card announced as an unpressed toggle.
              title={
                <Button
                  variant="title"
                  onClick={() => {
                    onOpen(module.programmeId, module.moduleId);
                  }}
                  data-testid={`assigned-title-${module.moduleId}`}
                >
                  {module.moduleTitle}
                </Button>
              }
              meta={
                <span className="flex items-center gap-1">
                  <Icon name="clock-counter-clockwise" aria-hidden />
                  {module.updatedAt ? (
                    <>
                      Updated <Time value={module.updatedAt} variant="date" />
                    </>
                  ) : (
                    "Not yet edited"
                  )}
                </span>
              }
              footer={
                <div className="flex items-center justify-between gap-2">
                  <ReviewBadge status={module.moduleReviewStatus} />
                  <MutedText size="caption">Open</MutedText>
                </div>
              }
              data-testid={`assigned-module-${module.moduleId}`}
            />
          </div>
        ))}
      </div>
    </>
  );
}
